import { useButton } from '@mui/base';
import cn from 'classnames';
import { forwardRef } from 'react';

import { LoaderIcon } from '../../foundations/icon/icons';
import { Tooltip } from '../../popovers/tooltip';

import type { ForwardedRef, MouseEvent } from 'react';

import type { ButtonProps } from './button.types';

import './button.scss';

function getTypeBasedClasses({
  active,
  color,
  disabled,
  hasIcons,
  loading,
  type,
}: {
  active: boolean;
  color: ButtonProps['color'];
  disabled: boolean;
  hasIcons: boolean;
  loading: boolean;
  type: ButtonProps['type'];
}): string[] {
  if (type === 'primary' && (color === 'default' || color === 'alternative')) {
    return [
      'rbui-button-primary',
      ...(active ? ['rbui-button-primary--active'] : []),
      ...(disabled ? ['rbui-button-primary--disabled'] : []),
      ...(loading ? ['rbui-button-primary--loading'] : []),
    ];
  }

  if (type === 'primary' && color === 'danger') {
    return [
      'rbui-button-primary-danger',
      ...(active ? ['rbui-button-primary-danger--active'] : []),
      ...(disabled ? ['rbui-button-primary-danger--disabled'] : []),
      ...(loading ? ['rbui-button-primary-danger--loading'] : []),
    ];
  }

  if (type === 'secondary' && (color === 'default' || color === 'alternative')) {
    return [
      'rbui-button-secondary',
      ...(active ? ['rbui-button-secondary--active'] : []),
      ...(disabled ? ['rbui-button-secondary--disabled'] : []),
      ...(loading ? ['rbui-button-secondary--loading'] : []),
    ];
  }

  if (type === 'secondary' && color === 'danger') {
    return [
      'rbui-button-secondary-danger',
      ...(active ? ['rbui-button-secondary-danger--active'] : []),
      ...(disabled ? ['rbui-button-secondary-danger--disabled'] : []),
      ...(loading ? ['rbui-button-secondary-danger--loading'] : []),
    ];
  }

  if (type === 'tertiary' && color === 'default') {
    return [
      'rbui-button-tertiary',
      ...(active ? ['rbui-button-tertiary--active'] : []),
      ...(disabled ? ['rbui-button-tertiary--disabled'] : []),
      ...(loading ? ['rbui-button-tertiary--loading'] : []),
      ...(hasIcons ? ['rbui-button-tertiary--has-icons'] : []),
    ];
  }

  if (type === 'tertiary' && color === 'danger') {
    return [
      'rbui-button-tertiary-danger',
      ...(active ? ['rbui-button-tertiary-danger--active'] : []),
      ...(disabled ? ['rbui-button-tertiary-danger--disabled'] : []),
      ...(loading ? ['rbui-button-tertiary-danger--loading'] : []),
      ...(hasIcons ? ['rbui-button-tertiary-danger--has-icons'] : []),
    ];
  }

  if (type === 'tertiary' && color === 'alternative') {
    return [
      'rbui-button-tertiary-alternative',
      ...(active ? ['rbui-button-tertiary-alternative--active'] : []),
      ...(disabled ? ['rbui-button-tertiary-alternative--disabled'] : []),
      ...(loading ? ['rbui-button-tertiary-alternative--loading'] : []),
      ...(hasIcons ? ['rbui-button-tertiary-alternative--has-icons'] : []),
    ];
  }

  if (type === 'inline') {
    return [
      'rbui-button-inline',
      ...(active ? ['rbui-button-inline--active'] : []),
      ...(disabled ? ['rbui-button-inline--disabled'] : []),
    ];
  }

  return [];
}

/**
 * The button allow users to take actions, and make choices, with a single tap.
 *
 * ## Usage
 * ```jsx
 * import { Button } from '@bloomreach/react-banana-ui';
 *
 * export default function MyComponent() {
 *   return <Button onClick={() => console.log('click')}>Button</Button>;
 * }
 * ```
 */
const Button = forwardRef((props: ButtonProps, forwardedRef: ForwardedRef<HTMLButtonElement>) => {
  const {
    children,
    className,
    color = 'default',
    disabled = false,
    endIcon,
    htmlType = 'button',
    loading = false,
    loadingText,
    onClick,
    startIcon,
    tooltip,
    type = 'secondary',
    ...reset
  } = props;

  const { active, focusVisible, getRootProps } = useButton({
    ...props,
    rootRef: forwardedRef,
    type: htmlType,
  });

  function handleOnClick(event: MouseEvent<HTMLButtonElement>): void {
    if (loading || disabled) {
      return;
    }

    onClick?.(event);
  }

  const rootProps = getRootProps({
    onClick: handleOnClick,
    ...reset,
  });

  const hasIcons = !!startIcon || !!endIcon;
  const iconOnly = !children && ((startIcon && !endIcon) || (endIcon && !startIcon));

  const classes = cn(
    'rbui-button',
    className,
    {
      'rbui-button--disabled': disabled,
      'rbui-button--focus-visible': focusVisible,
      'rbui-button--icon-only': iconOnly && !(loading && loadingText),
      'rbui-button--loading': loading,
    },
    getTypeBasedClasses({ active, color, disabled, hasIcons, loading, type }),
  );

  const loaderClasses = cn('rbui-button__loader', {
    'rbui-button__loader--short': loadingText || iconOnly,
  });

  return (
    <Tooltip title={tooltip}>
      <button {...rootProps} className={classes} type={htmlType}>
        {startIcon && !loading && (
          <span className="rbui-button__start-icon" role="img">
            {startIcon}
          </span>
        )}
        {loading && (
          <span className={loaderClasses} role="progressbar">
            <LoaderIcon className="rbui-button__spinner" />
          </span>
        )}
        {loading && loadingText}
        {!loading && children}
        {endIcon && !loading && (
          <span className="rbui-button__end-icon" role="img">
            {endIcon}
          </span>
        )}
      </button>
    </Tooltip>
  );
});

Button.displayName = 'Button';

export default Button;
